// Shake and directional kick (decision 0043; suggestion "shake and directional kick"): layered on top of the rig as
// an offset, never fed back into the follow or the arm.
//
// - Shake is trauma²: each impact adds trauma (0..1), which falls linearly on real time; the shake's size is
//   trauma squared, so small hits barely move and big ones shake hard. Its motion is smooth value noise on a time
//   quantised to `noiseHz` (60), so it looks the same at any refresh rate and in a capture. Translation along the
//   camera's right and up, plus roll and a little pitch and yaw.
// - The kick pushes the camera along a hit's direction by `size` metres (the game passes 0.25 × severity) and
//   decays as e^(−rate·t) on real time (rate 12 by default).
// - An FOV punch (optional): degrees added, decaying the same way.
// - The player's settings scale all of it: `strength` (the shake slider, 0..1) and `reduceMotion` (0 when on).
//   Either at zero leaves exactly no offset.
// Everything decays on real time: a hit-stop that freezes the sim doesn't freeze the shake (decision 0011's view clock).
import type { Vec3 } from "./math";

/** The shake's numbers, from the game. */
export interface ShakeSettings {
  /** The largest translation at full trauma (m). */
  offset: number;
  /** The largest roll, pitch and yaw at full trauma (rad). */
  roll: number;
  pitch: number;
  yaw: number;
  /** Trauma lost per real second (1/s): 1 / the longest shake's seconds. */
  decay: number;
  /** The noise's frequency (Hz): how fast the shake wobbles. */
  frequency: number;
  /** The noise time is quantised to this rate (Hz), so the shake is the same at any frame rate. */
  noiseHz: number;
  /** The kick's (and the FOV punch's) decay rate (1/s): offset × e^(−rate·t). */
  kickRate: number;
  /** The largest kick kept (m): stacked kicks never push the camera further. */
  kickMax: number;
  /** Trauma at a distance: full within `near` m of the camera's target, none past `far`. */
  near: number;
  far: number;
}

/** No shake at all (the rig's default until a game passes its numbers). */
export const NO_SHAKE: ShakeSettings = { offset: 0, roll: 0, pitch: 0, yaw: 0, decay: 1, frequency: 1, noiseHz: 60, kickRate: 12, kickMax: 0, near: 1, far: 2 };

/** What the shake adds this frame: a world offset (kick plus the shake's translation, already turned into world
 *  space by `apply`), angles (rad) and an FOV (deg). */
export interface ShakeOffset {
  offset: Vec3;
  roll: number;
  pitch: number;
  yaw: number;
  fov: number;
}

const ZERO: ShakeOffset = Object.freeze({ offset: Object.freeze({ x: 0, y: 0, z: 0 }), roll: 0, pitch: 0, yaw: 0, fov: 0 }) as ShakeOffset;

// smooth 1D value noise in [-1, 1]: hashed lattice values, smoothstep between them
const hash = (i: number, seed: number): number => {
  let h = Math.imul(i ^ Math.imul(seed, 0x9e3779b1), 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return ((h >>> 0) / 0xffffffff) * 2 - 1;
};
/** Smooth value noise at `t` (lattice spacing 1) for channel `seed`, in [-1, 1]. */
export function valueNoise(t: number, seed: number): number {
  const i = Math.floor(t);
  const f = t - i;
  const s = f * f * (3 - 2 * f);
  return hash(i, seed) * (1 - s) + hash(i + 1, seed) * s;
}

/** Shake and kick for one camera. Add impacts as they happen; `update(realDt)` once per rendered frame. */
export class CameraShake {
  settings: ShakeSettings;
  /** The player's shake strength (the settings slider), 0..1. */
  strength = 1;
  /** Reduce motion (decision 0063): no shake, no kick, no punch. */
  reduceMotion = false;
  private trauma = 0;
  private time = 0;
  private kickV: Vec3 = { x: 0, y: 0, z: 0 };
  private punch = 0;
  private last: ShakeOffset = ZERO;
  /** Impacts taken so far (trauma, kicks, punches), for a harness. */
  readonly counts = { trauma: 0, kicks: 0, punches: 0 };

  constructor(settings: ShakeSettings = NO_SHAKE) {
    this.settings = settings;
  }

  /** Add trauma (0..1; summed, capped at 1). With `at` and `from`, it falls off with the distance between them. */
  addTrauma(amount: number, at?: Vec3, from?: Vec3): void {
    if (!(amount > 0)) return;
    let k = 1;
    if (at && from) {
      const d = Math.hypot(at.x - from.x, at.y - from.y, at.z - from.z);
      const { near, far } = this.settings;
      k = d <= near ? 1 : d >= far ? 0 : 1 - (d - near) / (far - near);
    }
    this.trauma = Math.min(1, this.trauma + amount * k);
    this.counts.trauma++;
  }

  /** Push the camera `size` metres along `dir` (normalised here), decaying as e^(−kickRate·t). */
  kick(dir: Vec3, size: number): void {
    const l = Math.hypot(dir.x, dir.y, dir.z);
    if (!(size > 0) || l < 1e-9) return;
    this.kickV = { x: this.kickV.x + (dir.x / l) * size, y: this.kickV.y + (dir.y / l) * size, z: this.kickV.z + (dir.z / l) * size };
    const m = Math.hypot(this.kickV.x, this.kickV.y, this.kickV.z);
    const max = this.settings.kickMax;
    if (max > 0 && m > max) this.kickV = { x: (this.kickV.x / m) * max, y: (this.kickV.y / m) * max, z: (this.kickV.z / m) * max };
    this.counts.kicks++;
  }

  /** Widen the view by `degrees`, decaying like the kick. */
  fovPunch(degrees: number): void {
    if (degrees === 0) return;
    this.punch += degrees;
    this.counts.punches++;
  }

  /** The trauma now (0..1) and the kick's size now (m), before the player's settings. */
  level(): { trauma: number; kick: number; punch: number } {
    return { trauma: this.trauma, kick: Math.hypot(this.kickV.x, this.kickV.y, this.kickV.z), punch: this.punch };
  }

  /** Advance on real time; returns what to add this frame (in the camera's own axes for the shake: `right` and `up`
   *  turn it into world space). */
  update(realDt: number, right: Vec3, up: Vec3): ShakeOffset {
    const s = this.settings;
    const dt = Math.max(0, realDt);
    this.time += dt;
    // the kick's decay is applied before reading, so a kick added this frame starts at its full size
    const scale = this.reduceMotion ? 0 : Math.max(0, Math.min(1, this.strength));
    const shake = this.trauma * this.trauma * scale;
    const tq = Math.floor(this.time * s.noiseHz) / s.noiseHz;
    const n = (seed: number): number => valueNoise(tq * s.frequency, seed);
    const sx = shake * s.offset * n(1);
    const sy = shake * s.offset * n(2);
    const k = scale;
    const out: ShakeOffset =
      scale === 0
        ? ZERO
        : {
            offset: {
              x: this.kickV.x * k + right.x * sx + up.x * sy,
              y: this.kickV.y * k + right.y * sx + up.y * sy,
              z: this.kickV.z * k + right.z * sx + up.z * sy,
            },
            roll: shake * s.roll * n(3),
            pitch: shake * s.pitch * n(4),
            yaw: shake * s.yaw * n(5),
            fov: this.punch * k,
          };
    // then decay for the next frame
    this.trauma = Math.max(0, this.trauma - s.decay * dt);
    const e = Math.exp(-s.kickRate * dt);
    this.kickV = { x: this.kickV.x * e, y: this.kickV.y * e, z: this.kickV.z * e };
    this.punch *= e;
    if (Math.abs(this.punch) < 1e-6) this.punch = 0;
    this.last = out;
    return out;
  }

  /** What the last `update` returned. */
  current(): ShakeOffset {
    return this.last;
  }

  /** Stop everything now (a cut, a menu). */
  clear(): void {
    this.trauma = 0;
    this.kickV = { x: 0, y: 0, z: 0 };
    this.punch = 0;
    this.last = ZERO;
  }
}
